import type {
	ICellRendererParams,
	IRowNode,
	ValueFormatterParams,
} from 'ag-grid-community';

import {
	calcTotalForecast,
	oneDecimalFormatter,
	renderLiveData,
	twoDecimalRateFormatter,
	zeroDecimalFormatter,
} from '../tableCustomCellRender';
import type { ColumnObj } from '../useDashboardColumns';
import type { RecReviewData } from '../useDashboardController';

import { labels } from 'locales/en.label';
import { DEFAULT_COL_WIDTH } from './types';
import { liveDataComparator } from './utils';

export const metricsDefaultCols: ColumnObj[] = [
	{
		headerName: 'Available\nRooms',
		field: 'available_rooms_live',
		headerTooltip: labels.rec_review.dashboard.tooltip.available_rooms,
		maxWidth: DEFAULT_COL_WIDTH,
		valueFormatter: (params: ValueFormatterParams) => {
			return zeroDecimalFormatter(params);
		},
		cellRenderer: (params: ICellRendererParams) => {
			return renderLiveData(
				{ ...params },
				'available_rooms_live',
				'available_rooms',
				false,
				zeroDecimalFormatter,
			);
		},
	},
	{
		headerName: 'Transient\nCapacity',
		field: 'transient_capacity_live',
		headerTooltip: labels.rec_review.dashboard.tooltip.transient_cap,
		maxWidth: DEFAULT_COL_WIDTH,
		valueFormatter: (params: ValueFormatterParams) => {
			return zeroDecimalFormatter(params?.value);
		},
		cellRenderer: (params: ICellRendererParams) => {
			return renderLiveData(
				{ ...params },
				'transient_capacity_live',
				'transient_cap',
				false,
				zeroDecimalFormatter,
			);
		},
	},
	{
		headerName: 'Total Fcst\n(incl Grp)',
		field: 'total_fcst_incl_group',
		headerTooltip: labels.rec_review.dashboard.tooltip.total_fcst,
		maxWidth: DEFAULT_COL_WIDTH,
		valueFormatter: (params: ValueFormatterParams) => {
			if (!params.data) {
				return zeroDecimalFormatter(params?.value);
			}

			const { total_fcst_incl_group, total_fcst_incl_group_live } =
				calcTotalForecast(params.data) || {};

			if (total_fcst_incl_group_live) {
				return zeroDecimalFormatter(total_fcst_incl_group_live);
			}
			if (total_fcst_incl_group) {
				return zeroDecimalFormatter(total_fcst_incl_group);
			}
			return zeroDecimalFormatter(params?.value);
		},
		cellRenderer: (params: ICellRendererParams) => {
			const forecasts = calcTotalForecast(params.data);

			return renderLiveData(
				{ ...params, data: forecasts },
				'total_fcst_incl_group_live',
				'total_fcst_incl_group',
				false,
				zeroDecimalFormatter,
			);
		},
		comparator: (_valueA: undefined, _valueB: undefined, nodeA, nodeB) => {
			const getForecastValue = (node: IRowNode<RecReviewData>) => {
				const { total_fcst_incl_group, total_fcst_incl_group_live } =
					calcTotalForecast(node.data) || {};
				return total_fcst_incl_group_live ?? total_fcst_incl_group ?? 0;
			};
			const forecastA = getForecastValue(nodeA);
			const forecastB = getForecastValue(nodeB);

			return forecastA - forecastB;
		},
	},
	{
		headerName: 'OTB\n(incl Grp)',
		field: 'current_otb_live',
		headerTooltip: labels.rec_review.dashboard.tooltip.otb,
		maxWidth: DEFAULT_COL_WIDTH,
		valueFormatter: (params: ValueFormatterParams) => {
			return zeroDecimalFormatter(params?.value);
		},
		cellRenderer: (params: ICellRendererParams) => {
			return renderLiveData(
				{ ...params },
				'current_otb_live',
				'current_otb',
				false,
				zeroDecimalFormatter,
			);
		},
		comparator: liveDataComparator('current_otb'),
	},
	{
		headerName: 'ADR',
		field: 'adr_live',
		headerTooltip: labels.rec_review.dashboard.tooltip.adr,
		maxWidth: DEFAULT_COL_WIDTH,
		valueFormatter: (params: ValueFormatterParams) => {
			return twoDecimalRateFormatter(params?.value);
		},
		cellRenderer: (params: ICellRendererParams) => {
			return renderLiveData(
				{ ...params },
				'adr_live',
				'adr',
				true,
				twoDecimalRateFormatter,
			);
		},
		comparator: liveDataComparator('adr'),
	},
	{
		headerName: 'RevPAR',
		field: 'revpar_live',
		headerTooltip: labels.rec_review.dashboard.tooltip.revpar,
		maxWidth: DEFAULT_COL_WIDTH,
		cellRenderer: (params: ICellRendererParams) => {
			return renderLiveData(
				{ ...params },
				'revpar_live',
				'revpar',
				true,
				twoDecimalRateFormatter,
			);
		},
		valueFormatter: (params: ValueFormatterParams) => {
			return twoDecimalRateFormatter(params?.value);
		},
		comparator: liveDataComparator('revpar'),
	},
	{
		headerName: 'Curr Occ %\n(incl Grp)',
		field: 'occ_pct_live',
		headerTooltip: labels.rec_review.dashboard.tooltip.occ_pct,
		maxWidth: DEFAULT_COL_WIDTH,
		valueFormatter: (params: ValueFormatterParams) => {
			return oneDecimalFormatter(params?.value);
		},
		cellRenderer: (params: ICellRendererParams) => {
			return renderLiveData(
				{ ...params },
				'occ_pct_live',
				'occ_pct',
				false,
				oneDecimalFormatter,
			);
		},
		comparator: liveDataComparator('occ_pct'),
	},
	{
		headerName: 'Fcst Occ %\n(incl Grp)',
		field: 'fcst_occ_pct',
		headerTooltip: labels.rec_review.dashboard.tooltip.fcst_occ_pct,
		maxWidth: DEFAULT_COL_WIDTH,
		valueFormatter: (params: ValueFormatterParams) => {
			if (!params.data) {
				return oneDecimalFormatter(params?.value);
			}

			const { fcst_occ_pct, fcst_occ_pct_live } =
				calcTotalForecast(params.data) || {};

			if (fcst_occ_pct_live) {
				return oneDecimalFormatter(fcst_occ_pct_live);
			}
			if (fcst_occ_pct) {
				return oneDecimalFormatter(fcst_occ_pct);
			}
			return oneDecimalFormatter(params?.value);
		},
		cellRenderer: (params: ICellRendererParams) => {
			const forecasts = calcTotalForecast(params.data);

			return renderLiveData(
				{ ...params, data: forecasts },
				'fcst_occ_pct_live',
				'fcst_occ_pct',
				false,
				oneDecimalFormatter,
			);
		},
		comparator: (_valueA: undefined, _valueB: undefined, nodeA, nodeB) => {
			const getForecastValue = (node: IRowNode<RecReviewData>) => {
				const { fcst_occ_pct, fcst_occ_pct_live } =
					calcTotalForecast(node.data) || {};
				return fcst_occ_pct_live ?? fcst_occ_pct ?? 0;
			};
			const forecastA = getForecastValue(nodeA);
			const forecastB = getForecastValue(nodeB);

			return forecastA - forecastB;
		},
	},
];
